import { createAction } from 'redux-starter-kit';
import api from '../../api';
import inclusionsActions from './inclusions';

// Action types
export const setMissingDataStatus = createAction('missingData/setStatus');
export const setMissingDataUpdating = createAction('missingData/setUpdating');
export const clearMissingDataStatus = createAction('missingData/clearStatus');

/**
 * Notify the backend that a form modification occurred.
 * This schedules a missing data recalculation after the configured delay.
 *
 * @param {number} projectId - The project ID
 * @returns {Promise} - The API response
 */
export const notifyFormChange = (projectId) => async (dispatch) => {
  try {
    const res = await api.requestData(
      `projects/${projectId}/notify-form-change`,
      undefined,
      'post',
    );
    dispatch(setMissingDataStatus({ projectId, ...res }));
    return res;
  } catch (error) {
    console.error('Error notifying form change:', error);
    throw error;
  }
};

/**
 * Trigger an immediate missing data calculation for a project.
 * Used when user exits edit mode or switches tabs.
 *
 * @param {number} projectId - The project ID
 * @returns {Promise} - The API response
 */
export const triggerMissingDataCalculation = (projectId) => async (dispatch) => {
  try {
    dispatch(setMissingDataUpdating({ projectId, updating: true }));

    const res = await api.requestData(
      `projects/${projectId}/trigger-missing-data-calculation`,
      undefined,
      'post',
    );

    dispatch(setMissingDataStatus({ projectId, ...res }));
    dispatch(setMissingDataUpdating({ projectId, updating: false }));

    return res;
  } catch (error) {
    dispatch(setMissingDataUpdating({ projectId, updating: false }));
    console.error('Error triggering calculation:', error);
    throw error;
  }
};

/**
 * Get the current status of missing data calculation for a project.
 *
 * @param {number} projectId - The project ID
 * @returns {Promise} - The API response with status info
 */
export const getMissingDataStatus = (projectId) => async (dispatch) => {
  try {
    const res = await api.requestData(
      `projects/${projectId}/missing-data-status`,
      undefined,
      'get',
    );

    dispatch(setMissingDataStatus({ projectId, ...res }));

    return res;
  } catch (error) {
    console.error('Error getting missing data status:', error);
    throw error;
  }
};

/**
 * Compute missing data for a single inclusion.
 * Uses the existing inclusion read endpoint with force_missing_data_full_recount=true.
 *
 * @param {number} inclusionId - The inclusion ID
 * @returns {Promise} - The API response with updated inclusion data
 */
export const computeInclusionMissingData = (inclusionId) => async (dispatch) => {
  try {
    // Use the existing route with force_missing_data_full_recount parameter
    const res = await api.read('inclusions', inclusionId, {
      force_missing_data_full_recount: true,
    });

    // The read action already dispatches readSuccess which updates the store
    dispatch(inclusionsActions.readSuccess(res));

    return res;
  } catch (error) {
    console.error('Error computing inclusion missing data:', error);
    throw error;
  }
};

export default {
  setMissingDataStatus,
  setMissingDataUpdating,
  clearMissingDataStatus,
  notifyFormChange,
  triggerMissingDataCalculation,
  getMissingDataStatus,
  computeInclusionMissingData,
};
